/**
 * Call Availability Chip — TopBar widget (Apr 24, 2026)
 *
 * Spec: docs/integrations/CHATKART-CALL-HANDOFF-AURA-RESPONSE.md §4.4
 *
 * Three visible states (authenticated tenant users only):
 *   - "Available"    saffron dot, clickable (→ toggles to Unavailable)
 *   - "On call"      forest dot, NOT clickable (auto-flipped by CallHandoff
 *                    bridge; the user can't flip mid-call)
 *   - "Unavailable"  muted dot, clickable (→ attempts Available)
 *
 * Role gating: rendered only when `resolveRoleKey(user)` is one of
 *   OWNER · SALES_MANAGER · SALES_EXECUTIVE · TELECALLER · SERVICE_MANAGER
 *   · SERVICE_TECHNICIAN · FIELD_PARTNER
 * (spec calls out admin + sales + telecaller; service + field-partner added
 * because those departments also answer WhatsApp calls in practice.)
 *
 * Admin always sees it — `role.name === 'admin'` short-circuits.
 *
 * Error handling: if the user hits "Available" without a dialable phone
 * set, backend returns `code = 'DIALABLE_PHONE_REQUIRED'` — chip shows an
 * inline hint for 5s + a link to /profile.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhoneCall } from 'lucide-react';
import { api } from '@/utils/api';
import { useAuthStore } from '@/stores/auth';
import { resolveRoleKey, type DashboardRoleKey } from '@/pages/dashboard/roleLayouts';

// Roles that can toggle call availability. Keep in sync with backend
// expectations (the backend doesn't role-gate — tenant scope is enough —
// but showing the chip to finance/warehouse users would be noise).
const CALL_ROLE_KEYS: ReadonlySet<DashboardRoleKey> = new Set<DashboardRoleKey>([
  'OWNER',
  'SALES_MANAGER',
  'SALES_EXECUTIVE',
  'TELECALLER',
  'SERVICE_MANAGER',
  'SERVICE_TECHNICIAN',
  'FIELD_PARTNER',
]);

interface PresenceState {
  userId: string;
  available: boolean;
  onCall: boolean;
  dialablePhone: string | null;
  lastCallAt: string | null;
  updatedAt: string | null;
  configured: boolean;
}

type VisualState = 'available' | 'on_call' | 'unavailable' | 'loading';

function visualStateFor(p: PresenceState | null, loading: boolean): VisualState {
  if (loading || !p) return 'loading';
  if (p.onCall) return 'on_call';
  if (p.available) return 'available';
  return 'unavailable';
}

export function CallAvailabilityChip() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [presence, setPresence] = useState<PresenceState | null>(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [hint, setHint] = useState<string | null>(null);
  const hintTimerRef = useRef<number | null>(null);

  // ---------- gating (hook order matters — compute AFTER hooks) ----------
  const userRoleName = (user?.role?.name ?? '').toLowerCase();
  const isAdmin = userRoleName === 'admin';
  const roleKey = resolveRoleKey(user as never);
  const gated = isAdmin || CALL_ROLE_KEYS.has(roleKey);

  // Super admin (no tenantId) — presence is tenant-scoped, so don't show.
  const hasTenant = !!user?.tenantId;

  // ---------- initial fetch ----------
  useEffect(() => {
    if (!gated || !hasTenant) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    api
      .get('/presence/me')
      .then((res) => {
        if (!active) return;
        const d = res?.data?.data;
        if (d) setPresence(d as PresenceState);
      })
      .catch(() => {
        // Non-fatal — chip just shows "Unavailable" default.
        if (active) setPresence(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [gated, hasTenant, user?.id]);

  // Cleanup hint timer
  useEffect(() => {
    return () => {
      if (hintTimerRef.current) {
        window.clearTimeout(hintTimerRef.current);
        hintTimerRef.current = null;
      }
    };
  }, []);

  if (!gated || !hasTenant) return null;

  const vs = visualStateFor(presence, loading);

  // ---------- click handler ----------
  const handleClick = async () => {
    if (vs === 'on_call' || toggling) return; // can't toggle mid-call

    // If never configured and trying to go available → nudge to Profile.
    if (vs === 'unavailable' && (!presence || !presence.dialablePhone)) {
      showHint('Set your dial-able phone in Profile first.');
      hintTimerRef.current = window.setTimeout(() => {
        navigate('/profile');
      }, 600);
      return;
    }

    const targetEndpoint = vs === 'available' ? '/presence/unavailable' : '/presence/available';
    // Optimistic update
    const prev = presence;
    const optimistic: PresenceState = {
      userId: user?.id ?? '',
      available: vs !== 'available',
      onCall: false,
      dialablePhone: presence?.dialablePhone ?? null,
      lastCallAt: presence?.lastCallAt ?? null,
      updatedAt: new Date().toISOString(),
      configured: presence?.configured ?? true,
    };
    setPresence(optimistic);
    setToggling(true);

    try {
      const res = await api.post(targetEndpoint);
      const d = res?.data?.data;
      if (d) setPresence({ ...optimistic, ...(d as PresenceState), configured: true });
    } catch (err: any) {
      // Rollback
      setPresence(prev);
      const code = err?.response?.data?.code;
      const msg = err?.response?.data?.error;
      if (code === 'DIALABLE_PHONE_REQUIRED') {
        showHint('Set your dial-able phone in Profile first.');
        hintTimerRef.current = window.setTimeout(() => {
          navigate('/profile');
        }, 900);
      } else {
        showHint(msg || 'Couldn’t update availability.');
      }
    } finally {
      setToggling(false);
    }
  };

  const showHint = (msg: string) => {
    setHint(msg);
    if (hintTimerRef.current) {
      window.clearTimeout(hintTimerRef.current);
    }
    hintTimerRef.current = window.setTimeout(() => setHint(null), 3500);
  };

  // ---------- render ----------
  const styles = chipStyles(vs);
  const label = labelFor(vs);
  const title = titleFor(vs);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleClick}
        disabled={vs === 'on_call' || toggling}
        className="flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[12px]"
        style={{
          background: styles.bg,
          borderColor: styles.border,
          color: styles.ink,
          fontWeight: 500,
          opacity: toggling ? 0.7 : 1,
          cursor: vs === 'on_call' ? 'default' : 'pointer',
        }}
        title={title}
      >
        <span
          aria-hidden
          style={{
            width: 7,
            height: 7,
            borderRadius: 4,
            background: styles.dot,
            flexShrink: 0,
            boxShadow: vs === 'available' ? `0 0 0 2px ${styles.dotHalo}` : undefined,
          }}
        />
        <PhoneCall size={12} style={{ opacity: 0.7 }} />
        <span>{label}</span>
      </button>

      {hint && (
        <div
          className="absolute right-0 top-[calc(100%+6px)] rounded-md border px-2.5 py-1.5 text-[11px] shadow-lg"
          style={{
            background: 'var(--ivory-1)',
            borderColor: 'var(--line)',
            color: 'var(--ink-1)',
            boxShadow: 'var(--sh-3)',
            zIndex: 60,
            minWidth: 220,
            whiteSpace: 'nowrap',
          }}
        >
          {hint}
        </div>
      )}
    </div>
  );
}

// ─── Styling helpers ──────────────────────────────────────────────────────

function chipStyles(vs: VisualState) {
  switch (vs) {
    case 'available':
      return {
        bg: 'var(--saffron-50)',
        border: 'var(--saffron-100)',
        ink: 'var(--saffron-600)',
        dot: 'var(--saffron)',
        dotHalo: 'color-mix(in srgb, var(--saffron) 25%, transparent)',
      };
    case 'on_call':
      return {
        bg: 'var(--forest-50)',
        border: 'var(--forest-100)',
        ink: 'var(--forest-600)',
        dot: 'var(--forest)',
        dotHalo: 'transparent',
      };
    case 'loading':
    case 'unavailable':
    default:
      return {
        bg: 'var(--ivory-0)',
        border: 'var(--line)',
        ink: 'var(--ink-3)',
        dot: 'var(--ink-4)',
        dotHalo: 'transparent',
      };
  }
}

function labelFor(vs: VisualState): string {
  switch (vs) {
    case 'available':
      return 'Available';
    case 'on_call':
      return 'On call';
    case 'loading':
      return '…';
    case 'unavailable':
    default:
      return 'Unavailable';
  }
}

function titleFor(vs: VisualState): string {
  switch (vs) {
    case 'available':
      return 'You are available to take WhatsApp calls. Click to go Unavailable.';
    case 'on_call':
      return 'You are on a call — availability locks automatically until the call ends.';
    case 'loading':
      return 'Loading call availability…';
    case 'unavailable':
    default:
      return 'Click to become available for WhatsApp calls.';
  }
}
